import { Component, Input } from '@angular/core';
import {
  BowlingMode,
  BowlingParticipant,
  BowlingState
} from '../../../interfaces/bowling.interface';
import { MiniGameSession } from '../../../interfaces/mini-game.interface';

/**
 * Game-over screen — shown via `app-mini-game-summary` when
 * `BowlingState.phase === FINISHED`. Renders the winner banner +
 * margin + each side's stats. Pure presentational, all data is
 * derived from the bound `session` input.
 *
 * Lives alongside the generic mini-game summary; the parent
 * `MiniGameSummaryComponent` switches between game-specific summary
 * components based on `session.gameType` (`MiniGameType.BOWLING`
 * picks this one).
 */
@Component({
  selector: 'app-bowling-summary',
  templateUrl: './bowling-summary.component.html',
  styleUrls: ['./bowling-summary.component.scss']
})
export class BowlingSummaryComponent {
  /** Active mini-game session — provides `gameState` (cast to
   *  `BowlingState`), team rosters, and overall metadata. Null while
   *  the parent is still resolving the session. */
  @Input() session: MiniGameSession | null = null;
  /** Active language code ('ar' or 'en') for bilingual labels. */
  @Input() currLang: any = 'en';

  /** Type-narrow `gameState` to `BowlingState` (or null) — every
   *  template binding starts from this. */
  get state(): BowlingState | null {
    return (this.session?.gameState as BowlingState) ?? null;
  }

  /** The winning side, or null on a tie / before the game ends.
   *  Drives the "X wins!" copy. */
  get winner(): BowlingParticipant | null {
    const s = this.state;
    if (!s || s.winnerSide === null || s.winnerSide === 'tie') return null;
    return s.participants[s.winnerSide];
  }

  /** True when both sides finished with the same total. The template
   *  shows "It's a tie!" instead of a winner banner. */
  get isTie(): boolean {
    return this.state?.winnerSide === 'tie';
  }

  /** True for the head-to-head mode — drives layout choices in the
   *  template (1v1 may render player-centric stats vs team rosters). */
  get isOneVsOne(): boolean {
    return this.state?.mode === BowlingMode.ONE_VS_ONE;
  }

  /** Bilingual display-name pick — same currLang fork the rest of
   *  the bowling UI uses. Returns '' if no participant is passed. */
  getName(p: BowlingParticipant | null): string {
    if (!p) return '';
    return this.currLang === 'ar' ? p.displayName.ar : p.displayName.en;
  }

  /** Count of "strike" frames for one side — i.e. frames where the
   *  bowler knocked all 10 pins on their single roll. Used as a
   *  per-side stat in the summary. */
  countStrikes(p: BowlingParticipant): number {
    return p.frames.filter(f => f.roll1 === 10).length;
  }

  /** Winner banner copy. Bilingual; returns "It's a tie!" /
   *  "تعادل!" on a tie or "Name wins!" / "Name فاز!" otherwise. */
  winnerLabel(): string {
    if (this.isTie) return this.currLang === 'ar' ? 'تعادل!' : "It's a tie!";
    const w = this.winner;
    if (!w) return '';
    const name = this.getName(w);
    return this.currLang === 'ar' ? `${name} فاز!` : `${name} wins!`;
  }

  /** "by N pin(s)" / "بفارق N نقطة" — the score margin shown under
   *  the winner banner. Empty on a tie. Pluralisation handled in EN
   *  ("1 pin" vs "2 pins"); Arabic uses the same form regardless. */
  marginText(): string {
    if (!this.state || this.isTie) return '';
    const a = this.state.participants[0].totalScore;
    const b = this.state.participants[1].totalScore;
    const diff = Math.abs(a - b);
    return this.currLang === 'ar'
      ? `بفارق ${diff} نقطة`
      : `by ${diff} pin${diff === 1 ? '' : 's'}`;
  }
}
